// @ts-nocheck
/**
 * BillingScreen — abonnement SaaS du tenant : plan courant, consommation,
 * sièges et numéros, factures, choix de plan, portail Stripe.
 *
 * W4 (B2-billing, DES-21) :
 *   - réservé aux administrateurs (DESIGN.md §5 : « Facturation (tenant_admin) ») ;
 *     un viewer n'atteint jamais le dialer et le serveur le refuse, un
 *     commercial voit une ligne « réservé aux administrateurs » ;
 *   - jauge de consommation toujours visible, même à 0 (minutes du mois sur
 *     les minutes incluses, SMS, dépense du jour sur le plafond expliqué) ;
 *     une lecture en échec affiche « Indisponible », jamais 0 (DES-12) ;
 *   - numéros et sièges utilisés sur leur allocation ;
 *   - factures Stripe (GET /api/billing/invoices) ; sans Stripe, « Factures
 *     indisponibles » avec le code (STRIPE_NOT_CONFIGURED) ;
 *   - grille des plans repliée sous « Changer de plan » ;
 *   - « Nous contacter » (Enterprise) : mailto à la marque si VITE_SALES_EMAIL
 *     est défini, sinon le formulaire de contact de l'app (plus d'adresse tierce).
 */

import { useState } from 'react';
import { HUBSPOT_UI_ENABLED } from '@/lib/hubspotUi';
import { errorMessage, errorCodeOf } from '@/services/errorMessage';
import { motion } from 'framer-motion';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Check, Sparkles, Phone, CreditCard, Zap, Crown,
  AlertTriangle, Loader2, ExternalLink, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDialer } from '@/contexts/DialerContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/api/api-client';
import { listMembers } from '@/api/endpoints/admin';
import { listInvoices } from '@/api/endpoints/billing';
import { DataTable } from '@/components/shared/DataTable';
import { ContactKevinModal } from '@/components/common/ContactKevinButton';
import { currentLocale } from '@/i18n/locale';
import { BRAND } from '@/config/brand';

// ─── Plan definitions (mirror du backend PLAN_DEFINITIONS) ───────────────
// Price, label, description and features are interface copy, in the catalog
// (pages:billing.plans.<id>); only the icon and the highlight stay here.
// HubSpot hidden from the UI (AUDIT-INT-08, src/lib/hubspotUi.ts): its
// feature line is added only when the integration is shown.

// i18n-exempt-start: pricing data, not interface copy (see above)
const PLAN_DETAILS = {
  starter: { icon: Phone, extraFeatureKeys: HUBSPOT_UI_ENABLED ? ['pages:billing.plans.hubspotFeature'] : [] },
  pro: { icon: Zap, featured: true, extraFeatureKeys: [] },
  enterprise: { icon: Crown, extraFeatureKeys: [] },
};
// i18n-exempt-end

/** Minutes included per month (Subscription.ts header); null = fair-use, no gauge ceiling. */
export const INCLUDED_MINUTES = { starter: 500, pro: null, enterprise: null };

const STRIPE_NOT_CONFIGURED = 'STRIPE_NOT_CONFIGURED';

/** Sales address at the brand, set per deployment; without it « Nous contacter » opens the in-app contact form. */
const SALES_EMAIL = (import.meta.env?.VITE_SALES_EMAIL as string | undefined) || '';

// ─── Helpers ─────────────────────────────────────────────────────────────

// Colors stay fixed per status; the label is resolved from the catalog at
// render time (see `statusBadge` call sites, which now pass `t`).
const STATUS_STYLE = {
  trial:      { color: 'var(--vf-accent)', bg: 'var(--vf-accent-bg)' },
  active:     { color: 'var(--vf-green)',  bg: 'var(--vf-green-bg)' },
  past_due:   { color: 'var(--vf-orange)', bg: 'var(--vf-orange-bg)' },
  suspended:  { color: 'var(--vf-red)',    bg: 'var(--vf-red-bg)' },
  cancelled:  { color: 'var(--vf-text-3)', bg: 'var(--vf-surface-2)' },
};

const STATUS_LABEL_KEY = {
  trial: 'pages:billing.status.trial',
  active: 'common:states.active',
  past_due: 'pages:billing.status.pastDue',
  suspended: 'pages:billing.status.suspended',
  cancelled: 'pages:billing.status.cancelled',
};

function statusBadge(status, t) {
  const key = STATUS_LABEL_KEY[status] ? status : 'cancelled';
  return { ...STATUS_STYLE[key], label: t(STATUS_LABEL_KEY[key]) };
}

function planLabel(planId, t) {
  return t(`pages:billing.plans.${planId in PLAN_DETAILS ? planId : 'starter'}.label`);
}

function planDescription(planId, t) {
  return t(`pages:billing.plans.${planId in PLAN_DETAILS ? planId : 'starter'}.description`);
}

function formatEur(n) {
  return new Intl.NumberFormat(currentLocale(), { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
}

function formatMoney(minor, currency) {
  return new Intl.NumberFormat(currentLocale(), { style: 'currency', currency: (currency || 'eur').toUpperCase() }).format((minor || 0) / 100);
}

function formatDay(iso) {
  return iso ? new Date(iso).toLocaleDateString(currentLocale()) : '—';
}

function daysUntil(date) {
  if (!date) return null;
  const diff = new Date(date).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/** The usage days of the current calendar month (UTC date keys, YYYY-MM-DD). */
export function monthUsage(history, now = new Date()) {
  const month = now.toISOString().slice(0, 7);
  const days = (history || []).filter((d) => String(d.date || '').slice(0, 7) === month);
  return {
    minutes: days.reduce((a, d) => a + (d.calls_minutes || 0), 0),
    calls: days.reduce((a, d) => a + (d.calls_count || 0), 0),
    sms: days.reduce((a, d) => a + (d.sms_count || 0), 0),
    cost: days.reduce((a, d) => a + (d.total_cost_eur || 0), 0),
  };
}

// ─── Main ────────────────────────────────────────────────────────────────

export default function BillingScreen() {
  const { t } = useTranslation(['pages', 'common']);
  const { callerData } = useDialer();
  // 'tenant_admin' (or super admin) is the admin role; 'admin' no longer
  // exists, so every admin was told to ask an admin (2026-10-08).
  const { canChangePlan: isAdmin } = usePermissions();
  const organisationId = useAuthStore((s) => s.activeMembership?.organisationId);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [plansOpen, setPlansOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  // #37 -- « facturation Twilio ». Reserve aux super-admins, comme la route.
  const isSuperAdmin = callerData?.is_super_admin === true;
  const enabled = !!callerData && isAdmin;

  // Fetch subscription
  const { data: subData, isLoading: loadingSub, refetch: refetchSub } = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: async () => {
      const { data } = await api.functions.invoke('getBillingSubscription');
      return data;
    },
    enabled,
  });

  const subscription = subData?.subscription;
  const currentPlanId = subscription?.plan_id || 'starter';

  // Usage of the last 30 days (the month is taken from it).
  const usageQuery = useQuery({
    queryKey: ['billing-usage'],
    queryFn: async () => {
      const { data } = await api.functions.invoke('getBillingUsage', { days: 31 });
      return data;
    },
    enabled,
  });
  const numbersQuery = useQuery({
    queryKey: ['billing-numbers'],
    queryFn: async () => (await api.functions.invoke('listTenantNumbers')).data?.numbers ?? [],
    enabled,
  });
  const membersQuery = useQuery({
    queryKey: ['org-members', organisationId],
    queryFn: ({ signal }) => listMembers(organisationId, { signal }),
    enabled: enabled && !!organisationId,
  });
  const invoicesQuery = useQuery({
    queryKey: ['billing-invoices'],
    queryFn: ({ signal }) => listInvoices(signal),
    enabled,
    retry: false,
  });

  const usage = usageQuery.data ? monthUsage(usageQuery.data.history) : null;
  const today = usageQuery.data?.today;

  // Mutations
  const checkoutMutation = useMutation({
    mutationFn: async (planId) => {
      const { data } = await api.functions.invoke('createCheckoutSession', { plan_id: planId });
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err) => {
      const detail = errorMessage(err, t, 'pages:billing.errors.genericStripe');
      toast.error(t('pages:billing.errors.stripeOpenFailed', { detail }));
      setLoadingPlan(null);
    },
  });

  const portalMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.functions.invoke('createPortalSession');
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err) => {
      const detail = errorMessage(err, t, 'pages:billing.errors.generic');
      toast.error(t('pages:billing.errors.portalOpenFailed', { detail }));
    },
  });

  const handleSelectPlan = (planId) => {
    if (planId === 'enterprise') {
      if (SALES_EMAIL) window.open(`mailto:${SALES_EMAIL}?subject=${encodeURIComponent(`${BRAND.name} Enterprise`)}`, '_blank');
      else setContactOpen(true);
      return;
    }
    setLoadingPlan(planId);
    checkoutMutation.mutate(planId);
  };

  // Detection du retour de checkout (?checkout=success|cancelled)
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const checkoutStatus = urlParams?.get('checkout');

  if (checkoutStatus === 'success' && subscription?.status && subscription.status !== 'active') {
    setTimeout(() => refetchSub(), 2000);
  }

  if (!isAdmin) {
    return <p className="text-sm text-muted-foreground" data-testid="billing-admin-only">{t('pages:billing.adminOnly')}</p>;
  }

  if (loadingSub) {
    return (
      <div className="flex items-center justify-center py-20" role="status" aria-label={t('common:actions.loading')}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--vf-accent)' }} aria-hidden />
      </div>
    );
  }

  const badge = statusBadge(subscription?.status, t);
  const trialDaysLeft = subscription?.trial_ends_at ? daysUntil(subscription.trial_ends_at) : null;
  const includedMinutes = INCLUDED_MINUTES[currentPlanId] ?? null;
  const dialerNumbers = (numbersQuery.data || []).filter((n) => n.provider !== 'personal');
  const unavailable = t('pages:billing.unavailable');

  const invoiceColumns = [
    { id: 'number', header: t('pages:billing.invoices.number'), cell: (i) => <span className="font-mono text-xs">{i.number || i.id}</span> },
    { id: 'date', header: t('pages:billing.invoices.date'), cell: (i) => formatDay(i.created) },
    { id: 'period', header: t('pages:billing.invoices.period'), cell: (i) => (i.period_start ? `${formatDay(i.period_start)} – ${formatDay(i.period_end)}` : '—'), hideable: true },
    { id: 'amount', header: t('pages:billing.invoices.amount'), align: 'right', cell: (i) => <span className="tabular-nums">{formatMoney(i.amount_due, i.currency)}</span> },
    { id: 'status', header: t('pages:billing.invoices.status'), cell: (i) => t(`pages:billing.invoices.statuses.${['paid', 'open', 'draft', 'void', 'uncollectible'].includes(i.status) ? i.status : 'other'}`) },
    {
      id: 'link',
      header: <span className="sr-only">{t('pages:billing.invoices.open')}</span>,
      align: 'right',
      cell: (i) => (i.hosted_invoice_url || i.invoice_pdf ? (
        <a href={i.hosted_invoice_url || i.invoice_pdf} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-1 text-xs" style={{ color: 'var(--vf-accent-text)' }}
          aria-label={t('pages:billing.invoices.openAria', { number: i.number || i.id })}>
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />{t('pages:billing.invoices.open')}
        </a>
      ) : null),
    },
  ];
  const invoicesCode = invoicesQuery.isError ? errorCodeOf(invoicesQuery.error) : '';
  const stripeMissing = invoicesCode === STRIPE_NOT_CONFIGURED;

  return (
    <motion.div
      key="billing"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-6 max-w-6xl"
    >
      {/* Banner retour checkout */}
      {checkoutStatus === 'success' && (
        <div className="rounded-xl px-4 py-3 flex items-start gap-3"
          style={{ background: 'var(--vf-green-bg)', border: '1px solid var(--vf-green)', color: 'var(--vf-green)' }}>
          <Check className="w-5 h-5 mt-0.5 shrink-0" aria-hidden />
          <div className="flex-1 text-sm">
            <p className="font-medium">{t('pages:billing.checkoutSuccess.title')}</p>
            {/* 2026-05-27 -- honnete sur le delai : les numeros mobiles FR (07)
                sont valides par l'operateur, ce n'est pas instantane. */}
            <p className="mt-1 opacity-90">
              {t('pages:billing.checkoutSuccess.numbersPrefix')} <strong>{t('pages:billing.checkoutSuccess.activating')}</strong>{' '}
              {t('pages:billing.checkoutSuccess.activatingDetail')} <em>{t('pages:billing.checkoutSuccess.settingsPath')}</em>{t('pages:billing.checkoutSuccess.notifySuffix')}
            </p>
          </div>
        </div>
      )}
      {checkoutStatus === 'cancelled' && (
        <div className="rounded-xl px-4 py-3 flex items-center gap-3"
          style={{ background: 'var(--vf-orange-bg)', border: '1px solid var(--vf-orange)', color: 'var(--vf-orange)' }}>
          <AlertTriangle className="w-5 h-5" aria-hidden />
          <div className="flex-1 text-sm">{t('pages:billing.checkoutCancelled')}</div>
        </div>
      )}

      {/* Card plan courant */}
      <section className="rounded-xl border border-border bg-card p-6" aria-labelledby="billing-plan-h">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 id="billing-plan-h" className="text-lg font-semibold text-foreground">
                {t('pages:billing.currentPlanLabel', { plan: currentPlanId in PLAN_DETAILS ? planLabel(currentPlanId, t) : currentPlanId })}
              </h2>
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full"
                style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.color}` }}>
                {badge.label}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {subscription?.monthly_price_eur > 0
                ? t('pages:billing.priceSummary', {
                    price: formatEur(subscription.monthly_price_eur),
                    numbers: subscription?.max_numbers,
                    cap: formatEur(subscription?.daily_cost_cap_eur),
                  })
                : t('pages:billing.enterpriseQuote')}
            </p>
            {trialDaysLeft !== null && subscription?.status === 'trial' && (
              <p className="text-xs mt-2" style={{ color: 'var(--vf-accent)' }}>
                <Sparkles className="w-3.5 h-3.5 inline mr-1" aria-hidden />
                {trialDaysLeft > 0 ? t('pages:billing.trialDaysLeft', { count: trialDaysLeft }) : t('pages:billing.trialExpired')}
              </p>
            )}
            {subscription?.status === 'suspended' && (
              <p className="text-xs mt-2" style={{ color: 'var(--vf-red)' }}>
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" aria-hidden />
                {t('pages:billing.suspendedReasonPrefix')} {subscription.suspended_reason || t('pages:billing.suspendedReasonUnknown')}{t('pages:billing.suspendedContactSupport')}
              </p>
            )}
          </div>
          {subscription?.has_stripe_customer && (
            <Button
              variant="outline"
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
            >
              {portalMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden /> {t('common:actions.loading')}</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" aria-hidden /> {t('pages:billing.managePayment')}</>
              )}
            </Button>
          )}
        </div>
      </section>

      {/* Consommation — toujours visible, même à 0 (quick win 14) */}
      <section className="rounded-xl border border-border bg-card p-5" aria-labelledby="billing-usage-h" data-testid="billing-usage">
        <h2 id="billing-usage-h" className="text-sm font-semibold text-foreground">{t('pages:billing.usage.title')}</h2>
        <div className="mt-4 grid gap-5 md:grid-cols-3">
          <Gauge
            label={t('pages:billing.usage.minutes')}
            value={usage ? Math.round(usage.minutes) : null}
            max={includedMinutes}
            valueText={usage
              ? (includedMinutes ? t('pages:billing.usage.minutesOf', { used: Math.round(usage.minutes), included: includedMinutes }) : t('pages:billing.usage.minutesFairUse', { used: Math.round(usage.minutes) }))
              : unavailable}
          />
          <Gauge
            label={t('pages:billing.usage.sms')}
            value={usage ? usage.sms : null}
            max={null}
            valueText={usage ? t('pages:billing.usage.smsCount', { count: usage.sms }) : unavailable}
          />
          <Gauge
            label={t('pages:billing.usage.today')}
            value={usageQuery.data ? (today?.total_cost_eur || 0) : null}
            max={subscription?.daily_cost_cap_eur || null}
            valueText={usageQuery.data
              ? t('pages:billing.usage.todayOf', { spent: formatEur(today?.total_cost_eur || 0), cap: formatEur(subscription?.daily_cost_cap_eur) })
              : unavailable}
            hint={t('pages:billing.usage.capExplained', { cap: formatEur(subscription?.daily_cost_cap_eur) })}
          />
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          {usage ? t('pages:billing.usage.monthCost', { count: usage.calls, cost: formatEur(usage.cost), calls: usage.calls }) : unavailable}
        </p>
      </section>

      {/* Sièges et numéros sur leur allocation */}
      <section className="rounded-xl border border-border bg-card p-5" aria-labelledby="billing-alloc-h">
        <h2 id="billing-alloc-h" className="text-sm font-semibold text-foreground">{t('pages:billing.allocation.title')}</h2>
        <div className="mt-4 grid gap-5 md:grid-cols-2">
          <Gauge
            label={t('pages:billing.allocation.numbers')}
            value={numbersQuery.isSuccess ? dialerNumbers.length : null}
            max={subscription?.max_numbers || null}
            valueText={numbersQuery.isSuccess
              ? t('pages:billing.allocation.numbersOf', { used: dialerNumbers.length, max: subscription?.max_numbers ?? '—' })
              : unavailable}
          />
          <Gauge
            label={t('pages:billing.allocation.seats')}
            value={membersQuery.isSuccess ? membersQuery.data.length : null}
            max={null}
            valueText={membersQuery.isSuccess ? t('pages:billing.allocation.seatsCount', { count: membersQuery.data.length }) : unavailable}
            hint={t('pages:billing.allocation.seatsHint')}
          />
        </div>
      </section>

      {/* Factures (API Stripe) */}
      <section aria-labelledby="billing-invoices-h">
        <h2 id="billing-invoices-h" className="text-sm font-semibold text-foreground mb-2">{t('pages:billing.invoices.title')}</h2>
        {stripeMissing ? (
          <div className="rounded-xl border border-border bg-card px-5 py-4 text-sm" data-testid="billing-invoices-unavailable">
            <p>{t('pages:billing.invoices.unavailable')}</p>
            <p className="mt-1 font-mono text-[11px] text-muted-foreground">{invoicesCode}</p>
          </div>
        ) : (
          <DataTable
            caption={t('pages:billing.invoices.title')}
            columns={invoiceColumns}
            rows={invoicesQuery.data?.invoices ?? []}
            getRowId={(i) => i.id}
            status={invoicesQuery.isLoading ? 'loading' : invoicesQuery.isError ? 'error' : 'ready'}
            error={invoicesQuery.isError ? { message: t('pages:billing.invoices.loadFailed'), code: invoicesCode || undefined, onRetry: () => void invoicesQuery.refetch() } : undefined}
            empty={{
              title: t(invoicesQuery.data?.has_customer ? 'pages:billing.invoices.empty' : 'pages:billing.invoices.noCustomer'),
              action: <Button size="sm" variant="outline" onClick={() => setPlansOpen(true)}>{t('pages:billing.changePlan')}</Button>,
            }}
          />
        )}
      </section>

      {/* Plans, repliés sous « Changer de plan » */}
      <section aria-labelledby="billing-plans-h">
        <h2 id="billing-plans-h" className="m-0">
          <button
            type="button"
            className="inline-flex min-h-11 items-center gap-2 text-lg font-semibold text-foreground"
            aria-expanded={plansOpen}
            aria-controls="billing-plans"
            onClick={() => setPlansOpen((o) => !o)}
          >
            {t('pages:billing.changePlan')}
            <ChevronDown className={`h-4 w-4 transition-transform motion-reduce:transition-none ${plansOpen ? 'rotate-180' : ''}`} aria-hidden />
          </button>
        </h2>
        {plansOpen && (
          <div id="billing-plans" className="mt-2">
            <p className="text-sm text-muted-foreground mb-5">
              {t('pages:billing.plansIntro')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['starter', 'pro', 'enterprise'].map((planId) => (
                <PlanCard
                  key={planId}
                  planId={planId}
                  details={PLAN_DETAILS[planId]}
                  current={currentPlanId === planId}
                  canSelect
                  loading={loadingPlan === planId && checkoutMutation.isPending}
                  onSelect={() => handleSelectPlan(planId)}
                  t={t}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      <ContactKevinModal open={contactOpen} onOpenChange={setContactOpen} />

      {/* #37 -- consommation Twilio reelle (super-admin uniquement) */}
      {isSuperAdmin && <TwilioUsageReport t={t} />}
    </motion.div>
  );
}

/** A labelled meter; without a ceiling it shows the figure only (no bar). value null = « Indisponible ». */
function Gauge({ label, value, max, valueText, hint }: { label: string; value: number | null; max: number | null; valueText: string; hint?: string }) {
  const ratio = value !== null && max ? Math.min(1, value / max) : null;
  const tone = ratio === null ? 'var(--vf-accent)' : ratio >= 0.9 ? 'var(--vf-red)' : ratio >= 0.7 ? 'var(--vf-orange)' : 'var(--vf-accent)';
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-foreground tabular-nums">{valueText}</p>
      {ratio !== null && (
        <div
          role="meter"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={valueText}
          className="mt-2 h-2 w-full overflow-hidden rounded-full"
          style={{ background: 'var(--vf-surface-3)' }}
        >
          <div className="h-full rounded-full" style={{ width: `${Math.round(ratio * 100)}%`, background: tone }} />
        </div>
      )}
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** #37 -- consommation Twilio reelle du mois en cours (super-admin). */
function TwilioUsageReport({ t }) {
  const { data: twilioUsage, isLoading: loadingTwilioUsage, error: twilioUsageError } = useQuery({
    queryKey: ['twilio-usage-report'],
    queryFn: async () => {
      const { data } = await api.functions.invoke('getTwilioUsageReport');
      return data;
    },
    retry: false,
  });
  return (
    <div className="mt-8" data-testid="twilio-usage-report">
          <h2 className="text-sm font-semibold text-foreground mb-1">{t('pages:billing.twilioUsage.title')}</h2>
          <p className="text-xs text-muted-foreground mb-3">
            {t('pages:billing.twilioUsage.subtitle')}
          </p>

          {loadingTwilioUsage && (
            <p className="text-xs text-muted-foreground">{t('pages:billing.twilioUsage.loading')}</p>
          )}

          {twilioUsageError && (
            <p className="text-xs" style={{ color: 'var(--vf-red, #b91c1c)' }}>
              {t('pages:billing.errors.twilioReportUnavailable', { message: (twilioUsageError as Error)?.message || t('pages:billing.errors.unknown') })}
            </p>
          )}

          {twilioUsage?.totals && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <TuileTwilio titre={t('common:entity.call_plural')} valeur={`${twilioUsage.totals.calls.count}`}
                  detail={`${twilioUsage.totals.calls.minutes} min · ${twilioUsage.totals.calls.cost} ${twilioUsage.totals.currency}`} />
                <TuileTwilio titre="SMS" valeur={`${twilioUsage.totals.sms.count}`}
                  detail={`${twilioUsage.totals.sms.cost} ${twilioUsage.totals.currency}`} />
                <TuileTwilio titre={t('pages:billing.twilioUsage.recordings')} valeur={`${twilioUsage.totals.recordings.cost}`}
                  detail={twilioUsage.totals.currency} />
                <TuileTwilio titre={t('pages:billing.twilioUsage.totalMonth')} valeur={`${twilioUsage.totals.total_cost}`}
                  detail={`${twilioUsage.totals.currency} · ${twilioUsage.month}`} />
              </div>

              {Array.isArray(twilioUsage.per_tenant) && twilioUsage.per_tenant.length > 0 && (
                <div className="relative overflow-x-auto vf-scroll-visible rounded-lg border" style={{ borderColor: 'var(--vf-border)' }}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b" style={{ borderColor: 'var(--vf-border)' }}>
                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--vf-text-3)' }}>{t('pages:billing.twilioUsage.account')}</th>
                        <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--vf-text-3)' }}>{t('common:entity.call_plural')}</th>
                        <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--vf-text-3)' }}>{t('pages:billing.twilioUsage.minutes')}</th>
                        <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--vf-text-3)' }}>{t('pages:billing.twilioUsage.cost')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {twilioUsage.per_tenant.map((l) => (
                        <tr key={l.tenant_email} className="border-b last:border-0" style={{ borderColor: 'var(--vf-border)' }}>
                          <td className="px-3 py-2 text-foreground">{l.tenant_email}</td>
                          <td className="px-3 py-2 text-right font-mono">{l.call_count}</td>
                          <td className="px-3 py-2 text-right font-mono">{l.minutes}</td>
                          <td className="px-3 py-2 text-right font-mono">{l.cost} {twilioUsage.totals.currency}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
  );
}

/** #37 -- tuile de synthese du rapport Twilio. */
function TuileTwilio({ titre, valeur, detail }) {
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
      <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--vf-text-3)' }}>{titre}</p>
      <p className="text-lg font-semibold text-foreground mt-0.5">{valeur}</p>
      <p className="text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}

// ─── Sous-composants ─────────────────────────────────────────────────────

function PlanCard({ planId, details, current, canSelect, loading, onSelect, t }) {
  const Icon = details.icon;
  return (
    <div
      className="rounded-xl border p-5 flex flex-col"
      style={{
        background: details.featured ? 'var(--vf-accent-bg)' : 'var(--vf-bg)',
        borderColor: details.featured ? 'var(--vf-accent)' : 'var(--vf-border)',
        borderWidth: details.featured ? 2 : 1,
      }}
    >
      {details.featured && (
        <div className="mb-3">
          <span className="inline-block px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full"
            style={{ background: 'var(--vf-primary)', color: 'var(--vf-primary-fg)' }}>
            {t('pages:billing.plans.popular')}
          </span>
        </div>
      )}

      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-5 h-5" style={{ color: details.featured ? 'var(--vf-accent)' : 'var(--vf-text-2)' }} />
        <h3 className="text-base font-semibold text-foreground">{planLabel(planId, t)}</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{planDescription(planId, t)}</p>

      <div className="mb-4">
        <span className="text-3xl font-bold text-foreground">{t(`pages:billing.plans.${planId}.price`)}</span>
        {planId !== 'enterprise' && <span className="text-sm text-muted-foreground">{t('pages:billing.plans.perMonth')}</span>}
      </div>

      <ul className="space-y-2 mb-5 flex-1">
        {[...(t(`pages:billing.plans.${planId}.features`, { returnObjects: true }) as string[]), ...details.extraFeatureKeys.map((k) => t(k))].map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--vf-green)' }} />
            <span className="text-foreground/80">{f}</span>
          </li>
        ))}
      </ul>

      {current ? (
        <Button variant="outline" disabled className="w-full">
          {t('pages:billing.plans.current')}
        </Button>
      ) : (
        <Button
          onClick={onSelect}
          disabled={!canSelect || loading}
          className="w-full"
          style={details.featured ? {
            background: 'var(--vf-primary)',
            color: 'var(--vf-primary-fg)',
            border: 'none',
          } : undefined}
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('pages:billing.plans.redirecting')}</>
          ) : planId === 'enterprise' ? (
            <><ExternalLink className="w-4 h-4 mr-2" /> {t('pages:billing.plans.contactUs')}</>
          ) : (
            <>{t('pages:billing.plans.choose', { plan: planLabel(planId, t) })}</>
          )}
        </Button>
      )}
    </div>
  );
}
